import { Icon } from '@iconify/react/dist/iconify.js';
import { styled } from '@linaria/react';
import { Badge, Tabs } from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useHealthCheckCounts } from '../../api/integration-health-client/integration-health-client.hooks';
import { useTranslation } from 'react-i18next';
import { useOrganizationId, useRoles } from '../../helpers/auth-helpers/auth.hooks';
import { useFeatureFlag } from '../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../helpers/feature-flags/feature-flags.type';
import { useDocumentTitle } from '../../helpers/general-helpers';
import { useDataManagementStore } from '../../store/data-management-store/data-management-store';
import { Tab } from '../../store/data-management-store/data-management-store.type';
import { baseWhite, inkLight, secondaryBase, skyLightest } from '../../styles/design-tokens';
import { MenuDrivenContainerNoNavbar } from '../menu-driven-container/menu-driven-container-no-navbar';
import { BoardSettings } from './components/board-settings-tab/board-settings';
import { DataManagementHeader } from './components/data-management-header';
import { DataSources } from './components/data-sources-tab/data-sources';
import { IntegrationHealthTabContainer } from './components/integration-health-tab/integration-health-tab-container';
import { Portfolios } from './components/portfolios-tab/portfolios';
import { Projects } from './components/projects-tab/projects';
import { GroupsTab } from './components/groups-tab/groups-tab';
import { Workflows } from './components/workflows-tab/workflows';
import { getTabFromSearchParams } from './data-management.helpers';

export function DataManagement() {
  const i18n = useTranslation();
  useDocumentTitle('Data Management - Bloomfilter');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = useDataManagementStore((state) => state.activeTab);
  const setActiveTab = useDataManagementStore((state) => state.setActiveTab);
  const [dataManagementIsDirty, setDataManagementIsDirty] = useState(false);
  const isSyntheticProjectsEnabled = useFeatureFlag(FeatureFlag.SyntheticProjects);
  const isIntegrationHealthEnabled = useFeatureFlag(FeatureFlag.IntegrationHealthChecker);
  const { isAdmin } = useRoles();
  const showIntegrationHealth = isAdmin && isIntegrationHealthEnabled;
  const organizationId = useOrganizationId();
  const { data: healthCounts } = useHealthCheckCounts(organizationId ?? '', {
    enabled: !!organizationId && showIntegrationHealth,
  });
  const totalHealthIssues = healthCounts ? Object.values(healthCounts).reduce((sum, n) => sum + n, 0) : 0;

  const allowedTabs = useMemo(() => {
    const tabs = new Set<Tab>([Tab.DataSources, Tab.Portfolios, Tab.Workflows, Tab.BoardSettings]);
    if (isSyntheticProjectsEnabled) tabs.add(Tab.Projects);
    if (isAdmin) tabs.add(Tab.Groups);
    if (showIntegrationHealth) tabs.add(Tab.IntegrationHealthChecker);
    return tabs;
  }, [isSyntheticProjectsEnabled, isAdmin, showIntegrationHealth]);

  const handleTabChange = useCallback(
    (value: Tab) => {
      if (dataManagementIsDirty) {
        if (
          window.confirm(
            "This page is asking you to confirm that you want to leave - information you've entered may not be saved.",
          )
        ) {
          setDataManagementIsDirty(false);
          setActiveTab(value);
        }
      } else {
        setActiveTab(value);
      }
    },
    [dataManagementIsDirty, setActiveTab],
  );

  useEffect(() => {
    const tabFromUrl = getTabFromSearchParams(searchParams);
    if (tabFromUrl && allowedTabs.has(tabFromUrl)) {
      handleTabChange(tabFromUrl);
    }
  }, [searchParams, handleTabChange, allowedTabs]);

  useEffect(() => {
    if (!allowedTabs.has(activeTab)) {
      setActiveTab(Tab.DataSources);
    }
  }, [activeTab, allowedTabs, setActiveTab]);

  function handleClickBack() {
    if (dataManagementIsDirty) {
      if (
        window.confirm(
          "This page is asking you to confirm that you want to leave - information you've entered may not be saved.",
        )
      ) {
        setDataManagementIsDirty(false);
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  }

  return (
    <MenuDrivenContainerNoNavbar
      header={
        <DataManagementHeader
          title={i18n.t('data_management.data_management')}
          icon="account_tree"
          onClickBack={handleClickBack}
        />
      }
    >
      <StyledTabs variant="pills" radius="xl" value={activeTab} onChange={(value) => handleTabChange(value as Tab)}>
        <Tabs.List>
          <Tabs.Tab
            value={Tab.DataSources}
            leftSection={<Icon icon="material-symbols:data-object" />}
            color={secondaryBase}
          >
            {i18n.t('data_management.data_sources.data_sources')}
          </Tabs.Tab>
          <Tabs.Tab
            value={Tab.Portfolios}
            leftSection={<Icon icon="material-symbols:business-center" />}
            color={secondaryBase}
          >
            {i18n.t('common.portfolios')}
          </Tabs.Tab>
          {isSyntheticProjectsEnabled && (
            <Tabs.Tab
              value={Tab.Projects}
              leftSection={<Icon icon="material-symbols:rocket-launch" />}
              color={secondaryBase}
            >
              {i18n.t('common.projects')}
            </Tabs.Tab>
          )}
          <Tabs.Tab
            value={Tab.Workflows}
            leftSection={<Icon icon="material-symbols:account-tree" />}
            color={secondaryBase}
          >
            {i18n.t('data_management.workflows.workflows')}
          </Tabs.Tab>
          <Tabs.Tab
            value={Tab.BoardSettings}
            leftSection={<Icon icon="material-symbols:text-snippet" />}
            color={secondaryBase}
          >
            {i18n.t('data_management.board_settings.board_settings', {
              board: i18n.t('common.board'),
              settings: i18n.t('common.settings'),
            })}
          </Tabs.Tab>
          {isAdmin && (
            <Tabs.Tab value={Tab.Groups} leftSection={<Icon icon="material-symbols:group" />} color={secondaryBase}>
              {i18n.t('data_management.groups.groups')}
            </Tabs.Tab>
          )}
          {showIntegrationHealth && (
            <Tabs.Tab
              value={Tab.IntegrationHealthChecker}
              leftSection={<Icon icon="material-symbols:health-and-safety" />}
              color={secondaryBase}
              rightSection={
                totalHealthIssues > 0 ? (
                  <Badge size="xs" color="red" circle>
                    {totalHealthIssues}
                  </Badge>
                ) : undefined
              }
            >
              {i18n.t('data_management.integration_health_checker.integration_health_checker')}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value={Tab.DataSources}>
          <DataSources />
        </Tabs.Panel>
        <Tabs.Panel value={Tab.Portfolios}>
          <Portfolios />
        </Tabs.Panel>
        {isSyntheticProjectsEnabled && (
          <Tabs.Panel value={Tab.Projects}>
            <Projects />
          </Tabs.Panel>
        )}
        <Tabs.Panel value={Tab.Workflows}>
          <Workflows isDirty={dataManagementIsDirty} setIsDirty={setDataManagementIsDirty} />
        </Tabs.Panel>
        <Tabs.Panel value={Tab.BoardSettings}>
          <BoardSettings />
        </Tabs.Panel>
        {isAdmin && (
          <Tabs.Panel value={Tab.Groups}>
            <GroupsTab />
          </Tabs.Panel>
        )}
        {showIntegrationHealth && (
          <Tabs.Panel value={Tab.IntegrationHealthChecker}>
            <IntegrationHealthTabContainer />
          </Tabs.Panel>
        )}
      </StyledTabs>
    </MenuDrivenContainerNoNavbar>
  );
}

const StyledTabs = styled(Tabs)`
  display: flex;
  flex-direction: column;
  height: 100%;

  .mantine-Tabs-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .mantine-Tabs-list {
    gap: 8px;
  }

  .mantine-Tabs-tab {
    transition: all 0.2s ease;
    font-weight: 500;
    font-size: 14px;
    padding: 8px 16px;
    height: auto;

    &[data-active] {
      color: ${baseWhite};
      background: ${secondaryBase};
    }

    &:not([data-active]) {
      color: ${inkLight};
      border: 1px solid ${inkLight};

      &:hover {
        background: ${skyLightest};
        border: 1px solid ${secondaryBase};
      }
    }
  }
`;
